import {
  IntroActionUpdate,
  IntroDiscoveryUpdate,
  IntroPlanUpdate,
  IntroductionDetailResponseActionUpdates,
  IntroductionDetailResponseDiscoveryUpdates,
  IntroductionDetailResponsePlanUpdates,
  PlanningUpdateOutcomeEnum,
} from '@platform/client/schemas';
import { MultiSelectDropdown } from '@platform/components';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { formatDateStr } from '../../../util/DateHelpers';
import { snakeToTitleCase } from '../../../util/TitleCase';
import { AdminTable } from '../../AdminTable';
import { NoRecordsFoundTabContent, ProfileTabContainer } from '../../components/ProfileComponents';

type UpdateRow = {
  notes?: string | null;
  userName: string;
  userType: string;
  updateDate: string;
};

const UpdateSection = <T extends UpdateRow>({
  title,
  data,
  typeColumnHeader,
  getTypeLabel,
  filterOptions,
}: {
  title: string;
  data?: T[] | null;
  typeColumnHeader: string;
  getTypeLabel: (row: T) => string;
  filterOptions?: string[];
}) => {
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);

  const filteredData = useMemo(() => {
    if (!data) return [];
    if (!filterOptions || selectedTypes.length === 0) return data;
    return data.filter((row) => selectedTypes.includes(getTypeLabel(row)));
  }, [data, selectedTypes, filterOptions, getTypeLabel]);

  const columns = useMemo(() => {
    const columnHelper = createColumnHelper<T>();
    return [
      columnHelper.accessor((row) => row.userName, {
        id: 'userName',
        header: 'User',
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor((row) => row.userType, {
        id: 'userType',
        header: 'User Type',
        cell: (info) => info.getValue(),
        size: 25,
      }),
      columnHelper.accessor((row) => getTypeLabel(row), {
        id: 'type',
        header: typeColumnHeader,
        size: 25,
      }),
      columnHelper.accessor((row) => row.notes, {
        id: 'notes',
        header: 'Notes',
        cell: (info) => info.getValue(),
        size: 300,
      }),
      columnHelper.accessor((row) => row.updateDate, {
        id: 'updateDate',
        header: 'Date',
        cell: (info) => formatDateStr(new Date(info.getValue())),
        size: 25,
      }),
    ] as ColumnDef<T>[];
  }, [getTypeLabel, typeColumnHeader]);

  return (
    <div className='mb-6'>
      <div className='flex items-center justify-between mb-2'>
        <h3 className='text-lg font-semibold'>{title}</h3>
        {filterOptions && (
          <div className='w-64'>
            <MultiSelectDropdown
              placeholder='Filter by status'
              options={filterOptions.map((label) => ({ label }))}
              selectedItems={selectedTypes}
              onSelectionChange={setSelectedTypes}
            />
          </div>
        )}
      </div>
      {filteredData.length === 0 ?
        <NoRecordsFoundTabContent recordType='update' />
      : <AdminTable
          isError={false}
          isLoading={false}
          isSuccess={true}
          columns={columns}
          data={filteredData}
          isSimple
          filteredCount={filteredData.length}
        />
      }
    </div>
  );
};

export const UpdatesTabContent = ({
  discoveryUpdates,
  planUpdates,
  actionUpdates,
}: {
  discoveryUpdates?: IntroductionDetailResponseDiscoveryUpdates;
  planUpdates?: IntroductionDetailResponsePlanUpdates;
  actionUpdates?: IntroductionDetailResponseActionUpdates;
}) => {
  return (
    <ProfileTabContainer>
      <UpdateSection<IntroDiscoveryUpdate>
        title='Discovery update'
        data={discoveryUpdates}
        typeColumnHeader='Update Type'
        getTypeLabel={(row) => row.updateType}
      />
      <UpdateSection<IntroPlanUpdate>
        title='Plan Update'
        data={planUpdates?.filter(
          (update) => update.outcome !== PlanningUpdateOutcomeEnum.complete,
        )}
        typeColumnHeader='Status'
        getTypeLabel={(row) => snakeToTitleCase(row.outcome)}
        filterOptions={['Unresponsive', 'In Progress', 'Need Help']}
      />
      <UpdateSection<IntroActionUpdate>
        title='Action Update'
        data={actionUpdates}
        typeColumnHeader='Status'
        getTypeLabel={(row) => snakeToTitleCase(row.outcome)}
        filterOptions={['Complete', 'Unresponsive', 'In Progress', 'Different Update']}
      />
    </ProfileTabContainer>
  );
};
